import { FormEvent, useState } from 'react';
import { IconBan, IconSearch } from '../components/icons';
import { Empty, Skeleton, useAsync, useToast } from '../components/ui';
import { api, Suppression } from '../lib/api';
import { ago } from '../lib/format';
import { canWrite, useSession } from '../lib/session';
import { t } from '../lib/i18n';

const REASON: Record<string, string> = {
  hard_bounce: 'Жёсткий отскок',
  complaint: 'Жалоба на спам',
  unsubscribe: 'Отписка',
  manual: 'Вручную',
  mailbox_full_repeated: 'Ящик переполнен 3 раза за 30 дней',
};

export function SuppressionsPage() {
  const { me } = useSession();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const list = useAsync(() => api.get<Suppression[]>(`/suppressions${search ? `?q=${encodeURIComponent(search)}` : ''}`), [search]);
  const [email, setEmail] = useState('');
  const [scope, setScope] = useState<'all' | 'marketing'>('marketing');
  const admin = me?.user.role === 'owner' || me?.user.role === 'admin';

  async function add(e: FormEvent) {
    e.preventDefault();
    try {
      await api.post('/suppressions', { email, scope });
      setEmail('');
      toast(t('Адрес добавлен в подавления'));
      list.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }
  async function remove(s: Suppression) {
    const warn = s.reason === 'complaint' || s.reason === 'hard_bounce';
    if (!confirm(warn ? t('{email}: {reason}. Повторная отправка может ухудшить репутацию домена. Удалить всё равно?', { email: s.email, reason: t(REASON[s.reason] ?? s.reason) }) : t('Удалить {email}?', { email: s.email }))) return;
    await api.del(`/suppressions/${encodeURIComponent(s.email)}/${s.scope}`);
    list.reload();
  }

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Подавления')}</h1>
          <p>{t('Адреса, на которые мы не отправляем. Отскоки и жалобы блокируют все письма, отписка — только маркетинговые: сброс пароля дойдёт.')}</p>
        </div>
      </div>
      {canWrite(me) && (
        <form className="card card-pad row wrap" style={{ marginBottom: 16 }} onSubmit={add}>
          <input className="input" style={{ flex: 1, minWidth: 220 }} type="email" placeholder="user@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <div className="chips">
            <button type="button" className={`chip${scope === 'marketing' ? ' on' : ''}`} onClick={() => setScope('marketing')}>{t('Только маркетинг')}</button>
            <button type="button" className={`chip${scope === 'all' ? ' on' : ''}`} onClick={() => setScope('all')}>{t('Все письма')}</button>
          </div>
          <button className="btn btn-primary" disabled={!email}>{t('Добавить')}</button>
        </form>
      )}
      <div className="card">
        <div className="card-pad" style={{ borderBottom: '1px solid var(--border)' }}>
          <form onSubmit={(e) => (e.preventDefault(), setSearch(q.trim()))} style={{ position: 'relative' }}>
            <IconSearch size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--muted)' }} />
            <input className="input" style={{ paddingLeft: 36 }} placeholder={t('Поиск по адресу')} value={q} onChange={(e) => setQ(e.target.value)} />
          </form>
        </div>
        {list.loading ? (
          <div className="card-pad stack"><Skeleton /><Skeleton /></div>
        ) : !list.data?.length ? (
          <Empty icon={<IconBan size={36} />} title={t('Список чист')}>{t('Сюда автоматически попадают адреса с жёсткими отскоками и жалобами.')}</Empty>
        ) : (
          <table className="table">
            <thead><tr><th>{t('Адрес')}</th><th>{t('Область')}</th><th>{t('Причина')}</th><th>{t('Источник')}</th><th>{t('Когда')}</th><th /></tr></thead>
            <tbody>
              {list.data.map((s) => (
                <tr key={`${s.email}-${s.scope}`}>
                  <td><strong>{s.email}</strong></td>
                  <td><span className={`pill ${s.scope === 'all' ? 'bad' : 'warn'}`}>{s.scope === 'all' ? t('все письма') : t('маркетинг')}</span></td>
                  <td>{t(REASON[s.reason] ?? s.reason)}</td>
                  <td className="small muted"><code>{s.source}</code></td>
                  <td className="small muted">{ago(s.created_at)}</td>
                  <td style={{ textAlign: 'right' }}>{admin && <button className="btn btn-sm btn-danger" onClick={() => remove(s)}>{t('Удалить')}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
